import { useState, useEffect, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import isEqual from "lodash/isEqual";
import { Box, Button, Flex, Form, Toggle, toast, Spinner } from "dash-ui";
import {
  useGetCmsSettingsQuery,
  useUpdateCmsSettingsMutation,
  useResetCmsSettingsToDefaultsMutation,
} from "gql/generated.graphql";

import { CmsSettingsFormValues } from "./CmsSettingsContent.types";
import {
  CMS_SETTINGS_DEFAULTS,
  CMS_SETTINGS_LIMITS,
} from "./CmsSettingsContent.constants";
import {
  Container,
  TitleText,
  DescriptionText,
  SectionTitle,
  TransitionsContainer,
  FieldContainer,
} from "./CmsSettingsContent.styles";
import SettingsField from "./SettingsField";
import ResetConfirmationModal from "./ResetConfirmationModal";

const CmsSettingsContent = () => {
  const { t } = useTranslation(["common", "pages"]);
  const { t: tSettings } = useTranslation("pages", {
    keyPrefix: "cmsSettingsPage",
  });
  const { data, loading, refetch } = useGetCmsSettingsQuery();
  const [updateSettings, { loading: updating }] =
    useUpdateCmsSettingsMutation();
  const [resetSettings, { loading: resetting }] =
    useResetCmsSettingsToDefaultsMutation();

  const [showResetModal, setShowResetModal] = useState(false);
  const [formValues, setFormValues] = useState<CmsSettingsFormValues>(
    CMS_SETTINGS_DEFAULTS
  );
  const [originalValues, setOriginalValues] = useState<CmsSettingsFormValues>(
    CMS_SETTINGS_DEFAULTS
  );

  useEffect(() => {
    if (data?.getCmsSettings) {
      const settings = data.getCmsSettings;

      // Helper to get value with fallback to defaults
      const getValueOrDefault = <K extends keyof CmsSettingsFormValues>(
        field: K
      ): CmsSettingsFormValues[K] => {
        // Use nullish coalescing for boolean, OR operator for numbers
        if (field === "automatedTransitionsEnabled") {
          return (settings[field] ??
            CMS_SETTINGS_DEFAULTS[field]) as CmsSettingsFormValues[K];
        }
        return (settings[field] ||
          CMS_SETTINGS_DEFAULTS[field]) as CmsSettingsFormValues[K];
      };

      const newValues: CmsSettingsFormValues = {
        newListingToActiveDays: getValueOrDefault("newListingToActiveDays"),
        activeToAwaitingAuditDays: getValueOrDefault(
          "activeToAwaitingAuditDays"
        ),
        recentlyUpdatedToActiveDays: getValueOrDefault(
          "recentlyUpdatedToActiveDays"
        ),
        automatedTransitionsEnabled: getValueOrDefault(
          "automatedTransitionsEnabled"
        ),
      };

      setFormValues(newValues);
      setOriginalValues(newValues);
    }
  }, [data]);

  // Check if form values have changed from original values
  const hasChanges = useMemo(() => {
    return !isEqual(formValues, originalValues);
  }, [formValues, originalValues]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check if any required fields are empty
    if (
      formValues.newListingToActiveDays === "" ||
      formValues.activeToAwaitingAuditDays === "" ||
      formValues.recentlyUpdatedToActiveDays === ""
    ) {
      toast.error(tSettings("validationError"));
      return;
    }

    // Check if values are within valid range
    const isValid =
      formValues.newListingToActiveDays >= CMS_SETTINGS_LIMITS.MIN_DAYS &&
      formValues.newListingToActiveDays <= CMS_SETTINGS_LIMITS.MAX_DAYS &&
      formValues.activeToAwaitingAuditDays >= CMS_SETTINGS_LIMITS.MIN_DAYS &&
      formValues.activeToAwaitingAuditDays <= CMS_SETTINGS_LIMITS.MAX_DAYS &&
      formValues.recentlyUpdatedToActiveDays >= CMS_SETTINGS_LIMITS.MIN_DAYS &&
      formValues.recentlyUpdatedToActiveDays <= CMS_SETTINGS_LIMITS.MAX_DAYS;

    if (!isValid) {
      toast.error(tSettings("validationError"));
      return;
    }

    try {
      await updateSettings({
        variables: {
          input: {
            newListingToActiveDays: formValues.newListingToActiveDays,
            activeToAwaitingAuditDays: formValues.activeToAwaitingAuditDays,
            recentlyUpdatedToActiveDays: formValues.recentlyUpdatedToActiveDays,
            automatedTransitionsEnabled: formValues.automatedTransitionsEnabled,
          },
        },
      });
      toast.success(tSettings("updateSuccess"));
      refetch();
    } catch (error) {
      console.error("Settings update error:", error);
      toast.error(tSettings("updateError"));
    }
  };

  const handleReset = async () => {
    try {
      await resetSettings();
      toast.success(tSettings("resetSuccess"));
      setShowResetModal(false);
      refetch();
    } catch (error) {
      toast.error(tSettings("resetError"));
    }
  };

  const handleInputChange = useCallback(
    (field: string, value: number | boolean | "") => {
      setFormValues((prev) => ({
        ...prev,
        [field]: value,
      }));
    },
    []
  );

  if (loading) {
    return (
      <Container>
        <Flex justifyContent="center" alignItems="center" minHeight={400}>
          <Spinner />
        </Flex>
      </Container>
    );
  }

  return (
    <Container>
      <Form onSubmit={handleSubmit}>
        <Flex flexDirection="column" gap={4}>
          <Box mb={4}>
            <TitleText xl>{tSettings("title")}</TitleText>
            <Box mt={2}>
              <DescriptionText>{tSettings("description")}</DescriptionText>
            </Box>
          </Box>

          <Box mb={4} mt={2}>
            <Flex alignItems="center" gap={6}>
              <Toggle
                checked={formValues.automatedTransitionsEnabled}
                onChange={() =>
                  handleInputChange(
                    "automatedTransitionsEnabled",
                    !formValues.automatedTransitionsEnabled
                  )
                }
              />
              <TitleText>{tSettings("automatedTransitions")}</TitleText>
            </Flex>
            <Box mt={1}>
              <DescriptionText sm>
                {tSettings("automatedTransitionsDescription")}
              </DescriptionText>
            </Box>
          </Box>

          <TransitionsContainer
            disabled={!formValues.automatedTransitionsEnabled}
          >
            <SectionTitle lg>{tSettings("transitionTimes")}</SectionTitle>

            <Flex flexDirection="column" gap={6}>
              <FieldContainer>
                <SettingsField
                  label={tSettings("newListingToActive")}
                  helpText={tSettings("newListingToActiveHelp")}
                  value={formValues.newListingToActiveDays}
                  fieldName="newListingToActiveDays"
                  onChange={handleInputChange}
                  disabled={!formValues.automatedTransitionsEnabled || loading}
                />
              </FieldContainer>

              <FieldContainer>
                <SettingsField
                  label={tSettings("activeToAudit")}
                  helpText={tSettings("activeToAuditHelp")}
                  value={formValues.activeToAwaitingAuditDays}
                  fieldName="activeToAwaitingAuditDays"
                  onChange={handleInputChange}
                  disabled={!formValues.automatedTransitionsEnabled || loading}
                />
              </FieldContainer>

              <FieldContainer>
                <SettingsField
                  label={tSettings("recentlyUpdatedToActive")}
                  helpText={tSettings("recentlyUpdatedToActiveHelp")}
                  value={formValues.recentlyUpdatedToActiveDays}
                  fieldName="recentlyUpdatedToActiveDays"
                  onChange={handleInputChange}
                  disabled={!formValues.automatedTransitionsEnabled || loading}
                />
              </FieldContainer>
            </Flex>
          </TransitionsContainer>

          <Flex gap={10} mt={8}>
            <Button
              type="submit"
              variation="primary"
              disabled={loading || updating || resetting || !hasChanges}
              isLoading={updating}
              minWidth={150}
            >
              {t("common:labels.saveChanges")}
            </Button>
            <Button
              type="button"
              variation="outline"
              onClick={() => setShowResetModal(true)}
              disabled={loading || updating || resetting}
              minWidth={150}
            >
              {tSettings("resetToDefaults")}
            </Button>
          </Flex>
        </Flex>
      </Form>

      <ResetConfirmationModal
        isOpen={showResetModal}
        onClose={() => setShowResetModal(false)}
        onConfirm={handleReset}
        isLoading={resetting}
      />
    </Container>
  );
};

export default CmsSettingsContent;
